import { useState } from 'react';
import { 
  Anchor, Ship, Settings, Package, Hammer, Users, 
  ArrowRight, Target, Award, Shield, Lightbulb,
  Mail, Phone, MapPin, Send, Waves, Menu, X
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardHeader, CardContent } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import axios from 'axios';
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_eng-company/artifacts/viagltnz_SEA_CORE_logo_no_white_bg.jpg";

// Project Images from user uploaded photos
const PROJECT_IMAGES = [
  "https://customer-assets.emergentagent.com/job_eng-company/artifacts/7bxu1qti_image.png",
  "https://customer-assets.emergentagent.com/job_eng-company/artifacts/h9jymke4_image.png",
  "https://customer-assets.emergentagent.com/job_eng-company/artifacts/gken07d3_image.png",
  "https://customer-assets.emergentagent.com/job_eng-company/artifacts/cec5wn3d_image.png",
  "https://customer-assets.emergentagent.com/job_eng-company/artifacts/9j2q9j00_image.png"
];

function App() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...formData, subject: 'Website Inquiry' });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    }
    setIsSubmitting(false);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-sm shadow-sm" data-testid="main-navbar">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            <img src={LOGO_URL} alt="Sea Core" className="h-20" data-testid="navbar-logo" />
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {['Home', 'About', 'Services', 'Experience', 'Contact'].map((item) => (
                <button 
                  key={item} 
                  onClick={() => scrollToSection(item === 'Experience' ? 'projects' : item.toLowerCase())}
                  className="text-[#1A3A52] hover:text-[#B71C1C] font-medium transition-colors"
                  data-testid={`nav-link-${item.toLowerCase()}`}
                >
                  {item}
                </button>
              ))}
              <Button 
                onClick={() => scrollToSection('contact')}
                className="bg-[#B71C1C] hover:bg-[#C62828] text-white rounded-md px-6"
                data-testid="nav-cta-button"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-[#1A3A52]"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-gray-100" data-testid="mobile-menu">
              <div className="flex flex-col space-y-2 pt-4">
                {['Home', 'About', 'Services', 'Experience', 'Contact'].map((item) => (
                  <button 
                    key={item} 
                    onClick={() => scrollToSection(item === 'Experience' ? 'projects' : item.toLowerCase())}
                    className="text-[#1A3A52] hover:text-[#B71C1C] font-medium py-2 text-left transition-colors"
                    data-testid={`mobile-nav-link-${item.toLowerCase()}`}
                  >
                    {item}
                  </button>
                ))}
                <Button 
                  onClick={() => scrollToSection('contact')}
                  className="bg-[#B71C1C] hover:bg-[#C62828] text-white rounded-md mt-2"
                  data-testid="mobile-nav-cta"
                >
                  Get Started
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden bg-[#1A3A52]" data-testid="hero-section">
        {/* Background Glow Effects */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#B71C1C] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#1B5E20] rounded-full filter blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-white space-y-6">
                <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                  <Anchor className="w-4 h-4 text-[#B71C1C]" />
                  <span className="text-sm font-medium">MARINE & OFFSHORE ENGINEERING</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight" data-testid="hero-title">
                  Delivering Engineering-Driven Excellence Across the Seas
                </h1>
                
                <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
                  SeaCore Solutions is a marine and offshore engineering company delivering integrated technical services, 
                  ship repair, and project execution support. Our strength lies in strong technical leadership, hands-on 
                  operational experience, and strict compliance with safety and class requirements.
                </p>

                <p className="text-base text-gray-400 italic border-l-2 border-[#B71C1C] pl-4">
                  Your Trusted Technical Partner for Marine & Offshore Engineering, Ship Repair, and Project Execution.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    onClick={() => scrollToSection('services')}
                    className="bg-[#B71C1C] hover:bg-[#C62828] text-white px-8 group"
                    data-testid="hero-cta-services"
                  >
                    Our Services
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Button 
                    onClick={() => scrollToSection('contact')}
                    variant="outline" 
                    className="border-white text-white hover:bg-white hover:text-[#1A3A52] px-8"
                    data-testid="hero-cta-contact"
                  >
                    Contact Us
                  </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/20">
                  <div>
                    <div className="text-2xl font-bold text-[#B71C1C]">Integrated</div>
                    <div className="text-sm text-gray-300">Technical Services</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#B71C1C]">24/7</div>
                    <div className="text-sm text-gray-300">Fast Response</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#B71C1C]">Class</div>
                    <div className="text-sm text-gray-300">Compliant</div>
                  </div>
                </div>
              </div>

              {/* Right - Logo Display (Perfect Circle) */}
              <div className="relative hidden md:flex items-center justify-center">
                <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#B71C1C] rounded-full filter blur-3xl opacity-40"></div>
                <div className="absolute -bottom-12 -left-12 w-56 h-56 bg-[#1B5E20] rounded-full filter blur-3xl opacity-40"></div>
                <div className="relative z-10 bg-white flex items-center justify-center p-10 shadow-2xl" style={{width: '500px', height: '500px', borderRadius: '50%', overflow: 'hidden'}}>
                  <img src={LOGO_URL} alt="Sea Core" className="max-w-full max-h-full object-contain" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-16 md:h-24 fill-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 md:py-28 bg-gray-50" data-testid="about-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A3A52] mb-4">About SeaCore Solutions</h2>
            <div className="w-20 h-1 bg-[#B71C1C] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600">Engineering-driven excellence in marine and offshore technical services</p>
          </div>

          {/* Vision & Mission */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <Card className="border-t-4 border-t-[#B71C1C] hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <Target className="w-12 h-12 text-[#B71C1C] mb-4" />
                <h3 className="text-2xl font-bold text-[#1A3A52] mb-4">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed">
                  To become a world-class marine and offshore service partner, recognized for excellence, 
                  reliability, and innovation in delivering sustainable maritime solutions across the global industry.
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-[#1B5E20] hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <Award className="w-12 h-12 text-[#1B5E20] mb-4" />
                <h3 className="text-2xl font-bold text-[#1A3A52] mb-4">Our Mission</h3>
                <ul className="text-gray-600 leading-relaxed space-y-3">
                  {[
                    "To deliver high-quality marine, offshore, and engineering services that meet the highest international standards of safety, quality, and performance.",
                    "To build long-term partnerships with clients through integrity, professionalism, and consistent value creation.",
                    "To continuously enhance our capabilities through innovation, technology, and the development of highly skilled professionals.",
                    "To support safe, efficient, and sustainable maritime operations while contributing to the growth of the global marine industry."
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-[#1B5E20] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Team Leadership Paragraph */}
          <div className="bg-white border-l-4 border-[#B71C1C] p-6 mb-16 rounded-r-lg shadow-sm">
            <p className="text-gray-700 leading-relaxed">
              With a team led by experienced Marine Engineers, Chief Engineers, Technical Superintendents, Docking Managers, 
              and Technical Directors, SeaCore Solutions combines practical field expertise with structured engineering and 
              project management approaches to deliver reliable and compliant solutions for marine and offshore operations.
            </p>
          </div>

          {/* Core Values */}
          <div className="mb-16">
            <h3 className="text-2xl md:text-3xl font-bold text-center text-[#1A3A52] mb-12">Our Core Values</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Shield, title: "Safety First", desc: "Upholding strict HSE standards with a zero-incident commitment across all operations and project sites." },
                { icon: Award, title: "Excellence", desc: "Delivering engineering-driven solutions with precision, reliability, and full accountability for results." },
                { icon: Target, title: "Integrity", desc: "Conducting business with transparency, ethical responsibility, and professional accountability at every level." },
                { icon: Lightbulb, title: "Innovation", desc: "Continuously improving capabilities through technology adoption and skilled professional development." }
              ].map((item, i) => (
                <Card key={i} className="text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-2 border-0 bg-white" data-testid={`value-card-${i}`}>
                  <CardContent className="p-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1A3A52]/5 rounded-full mb-4">
                      <item.icon className="w-8 h-8 text-[#B71C1C]" />
                    </div>
                    <h4 className="text-xl font-bold text-[#1A3A52] mb-3">{item.title}</h4>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="mb-16">
            <h3 className="text-2xl md:text-3xl font-bold text-center text-[#1A3A52] mb-12">Why Choose Us</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "Strong Technical Leadership", desc: "Senior technical leadership with extensive hands-on experience across marine operations, ship repair, docking projects, and offshore environments, ensuring sound engineering decisions and effective execution." },
                { title: "Proven Experience", desc: "Demonstrated track record on high-risk vessels including oil tankers, gas tankers, and offshore units, with strict adherence to safety protocols and classification society requirements." },
                { title: "Fast Response", desc: "Operational agility with rapid mobilization capability and efficient execution to meet urgent project timelines and critical operational requirements." },
                { title: "Quality Standards", desc: "Consistent quality delivery aligned with international maritime standards, classification requirements, and industry best practices." },
                { title: "Competitive Costing", desc: "Value-driven solutions with transparent pricing structures, ensuring cost-effectiveness without compromising on quality or safety." },
                { title: "Customer-Centric", desc: "Dedicated focus on understanding client requirements and building long-term partnerships through consistent value creation and reliable service delivery." }
              ].map((item, i) => (
                <Card key={i} className="hover:shadow-lg transition-all duration-300 border-l-4 border-l-[#B71C1C]" data-testid={`why-choose-card-${i}`}>
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#1A3A52] mb-2">{item.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* HSE Commitment */}
          <div className="bg-[#1A3A52] rounded-2xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">HSE & Quality Commitment</h3>
              <p className="text-white/80 max-w-3xl mx-auto leading-relaxed">
                We are fully committed to maintaining high standards of Health, Safety, Environment (HSE), and Quality 
                across all marine and offshore operations. Our projects are executed in alignment with applicable 
                international safety regulations, class requirements, and industry best practices.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-[#B71C1C] mb-2">Zero Incident</div>
                <div className="text-white/80">Commitment</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#B71C1C] mb-2">Safety</div>
                <div className="text-white/80">Compliance Commitment</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#B71C1C] mb-2">Customer</div>
                <div className="text-white/80">Satisfaction Focus</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 md:py-28 bg-white" data-testid="services-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A3A52] mb-4">Our Services</h2>
            <div className="w-20 h-1 bg-[#B71C1C] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600">
              Comprehensive marine and offshore technical services delivered with engineering precision, safety compliance, and operational reliability
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Ship, title: "Shipbuilding & Ship Repair", desc: "Comprehensive hull, mechanical, piping, electrical, and deck machinery services with strict quality control and class compliance for all vessel types." },
              { icon: Waves, title: "Marine & Offshore Engineering", desc: "Integrated engineering solutions with strong focus on safety, compliance, reliability, and lifecycle performance across marine and offshore assets." },
              { icon: Settings, title: "Mechanical & Electrical Works", desc: "Professional M&E services including installation, maintenance, troubleshooting, and system optimization ensuring operational reliability and safety compliance." },
              { icon: Package, title: "Marine Supply & Trading", desc: "Reliable sourcing and delivery of certified marine equipment, spare parts, and consumables with full documentation and quality assurance." },
              { icon: Hammer, title: "Carpentry & Joinery", desc: "High-quality marine carpentry for interior and exterior vessel areas, delivering durability, functionality, and professional finishing standards." },
              { icon: Users, title: "Engine & Pump Overhaul", desc: "Complete overhaul services for main engines, auxiliary engines, pumps, valves, boilers, and electrical motors with performance testing and certification." }
            ].map((service, i) => (
              <Card key={i} className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-0 bg-white" data-testid={`service-card-${i}`}>
                <CardHeader>
                  <div className="w-12 h-12 bg-[#1A3A52] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#B71C1C] transition-colors duration-300">
                    <service.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg text-[#1A3A52] group-hover:text-[#B71C1C] transition-colors leading-tight">
                    {service.title}
                  </h3>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm leading-relaxed">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Services */}
          <div className="bg-gray-50 rounded-2xl p-8 mb-12">
            <h3 className="text-xl font-bold text-[#1A3A52] mb-2 text-center">Additional Specialized Services</h3>
            <p className="text-gray-600 text-center mb-6 text-sm">
              Our specialized services support critical onboard systems and are executed by experienced personnel with proven field expertise and strict quality control.
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-700">
              <div>
                <p className="font-semibold mb-2">Engine Services:</p>
                <ul className="space-y-1">
                  <li>• Main Engine Cleaning</li>
                  <li>• Engine Overhaul</li>
                  <li>• AE Cooler Service</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Pump & Valve Services:</p>
                <ul className="space-y-1">
                  <li>• Pump Overhaul</li>
                  <li>• Cargo Pump Turbine Overhaul</li>
                  <li>• Ship Valve & P/V Valve Service</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Electrical & Boiler:</p>
                <ul className="space-y-1">
                  <li>• Electrical Motor Overhaul</li>
                  <li>• Electrical Service Overhaul</li>
                  <li>• Boiler Re-tubing Service</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gray-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-[#1A3A52] mb-3">Need Marine or Offshore Technical Services?</h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Our experienced technical team is ready to support your project with engineering expertise, fast response, and reliable quality delivery.
            </p>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-[#B71C1C] hover:bg-[#C62828] text-white px-8 group"
              data-testid="services-contact-cta"
            >
              Contact Us Today
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 md:py-28 bg-gray-50" data-testid="projects-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A3A52] mb-4">Experience & Capabilities</h2>
            <div className="w-20 h-1 bg-[#B71C1C] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600">
              Selected examples of our technical experience across marine and offshore projects, demonstrating our capabilities in high-risk and regulated environments.
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {['All', 'Ship Repair', 'Offshore Engineering'].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  activeFilter === filter 
                    ? 'bg-[#B71C1C] text-white shadow-lg' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
                data-testid={`filter-${filter.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {filter}
              </button>
            ))}
          </div>

          {/* Project Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Boiler & Cooler Services", category: "Ship Repair", scope: "Overhaul & Maintenance", desc: "Boiler re-tubing, AE cooler service, and heat exchanger maintenance for marine vessels.", img: PROJECT_IMAGES[0] },
              { title: "Engine Overhaul", category: "Ship Repair", scope: "Major Overhaul", desc: "Complete overhaul of 2-stroke and 4-stroke main engines and auxiliary engines with performance testing.", img: PROJECT_IMAGES[1] },
              { title: "Pump Overhaul", category: "Ship Repair", scope: "Overhaul & Testing", desc: "Comprehensive pump overhaul including centrifugal, gear, and cargo pump turbines with certification.", img: PROJECT_IMAGES[2] },
              { title: "Valve Overhaul", category: "Ship Repair", scope: "Service & Certification", desc: "Ship valve overhaul and P/V valve service for cargo tanks, fuel systems, and venting systems.", img: PROJECT_IMAGES[3] },
              { title: "Electrical Service & Motor Overhaul", category: "Offshore Engineering", scope: "Electrical Systems", desc: "Electrical motor overhaul, switchboard inspection, stator rewinding, and electrical system maintenance.", img: PROJECT_IMAGES[4] }
            ].map((project, i) => (
              <Card key={i} className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-0" data-testid={`project-card-${i}`}>
                <div className="relative overflow-hidden h-56 bg-gray-200">
                  <img 
                    src={project.img} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A3A52]/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="inline-block bg-[#B71C1C] text-white text-xs px-2.5 py-0.5 rounded-md font-semibold mb-2">
                        {project.category}
                      </span>
                    </div>
                  </div>
                </div>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#1B5E20] bg-[#1B5E20]/10 text-xs px-2.5 py-0.5 rounded-md font-semibold">
                      {project.scope}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-[#1A3A52] mb-2 group-hover:text-[#B71C1C] transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{project.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-28 bg-white" data-testid="contact-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A3A52] mb-4">Get In Touch</h2>
            <div className="w-20 h-1 bg-[#B71C1C] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600">
              For technical inquiries, project discussions, or partnership opportunities, please contact us using the form below.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#B71C1C]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#B71C1C]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A3A52] mb-2">Email</h3>
                      <a href="mailto:info@seacoresolutions.com" className="text-gray-600 hover:text-[#B71C1C] transition-colors" data-testid="contact-email-link">
                        info@seacoresolutions.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#1B5E20]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#1B5E20]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A3A52] mb-2">Phone</h3>
                      <a href="tel:+628117000610" className="text-gray-600 hover:text-[#1B5E20] transition-colors" data-testid="contact-phone-link">
                        +62 811 7000 6101
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#1A3A52]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A3A52] mb-2">Location</h3>
                      <p className="text-gray-600 text-sm">
                        Komplek Cipta Grand City<br />
                        Blok K No 17 & No 18<br />
                        Sungai Binti, Sagulung<br />
                        Batam City, Kepulauan Riau<br />
                        Indonesia
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg mb-6" data-testid="success-message">
                      Thank you for your message! We will contact you soon.
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6" data-testid="error-message">
                      Failed to send message. Please try again.
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-[#1A3A52] mb-2">Full Name *</label>
                        <Input 
                          required
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="border-gray-300 focus:border-[#B71C1C] focus:ring-[#B71C1C]"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#1A3A52] mb-2">Email Address *</label>
                        <Input 
                          type="email"
                          required
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="border-gray-300 focus:border-[#B71C1C] focus:ring-[#B71C1C]"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-[#1A3A52] mb-2">Phone Number</label>
                        <Input 
                          type="tel"
                          placeholder="+62 xxx xxxx xxxx"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="border-gray-300 focus:border-[#B71C1C] focus:ring-[#B71C1C]"
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#1A3A52] mb-2">Company Name</label>
                        <Input 
                          placeholder="Your Company"
                          value={formData.company}
                          onChange={(e) => setFormData({...formData, company: e.target.value})}
                          className="border-gray-300 focus:border-[#B71C1C] focus:ring-[#B71C1C]"
                          data-testid="input-company"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#1A3A52] mb-2">Message *</label>
                      <Textarea 
                        required
                        rows={6}
                        placeholder="Tell us about your project requirements..."
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="border-gray-300 focus:border-[#B71C1C] focus:ring-[#B71C1C] resize-none"
                        data-testid="input-message"
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#B71C1C] hover:bg-[#C62828] text-white group"
                      data-testid="submit-button"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A3A52] text-white py-12" data-testid="main-footer">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <img src={LOGO_URL} alt="Sea Core" className="h-28 mb-4" />
              <p className="text-white/70 text-sm leading-relaxed max-w-md">
                Delivering excellence across the seas with integrated engineering, ship repair, 
                and technical support solutions.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-white/70">
                {['Home', 'About', 'Services', 'Projects', 'Contact'].map((item) => (
                  <li key={item}>
                    <button 
                      onClick={() => scrollToSection(item.toLowerCase())}
                      className="hover:text-[#B71C1C] transition-colors"
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li>+62 811 7000 6101</li>
                <li>info@seacoresolutions.com</li>
                <li>Batam City, Indonesia</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-sm text-white/60">
            <p>© {new Date().getFullYear()} SEA CORE ENGINEERING SOLUTION. All rights reserved.</p>
            <p className="mt-2">www.seacoresolutions.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
